import { Employee, OperationalTask, WorkTask } from "../types/ganttChart";
import { FullTimelineRange } from "../types/timeline";

type TaskInterval = {
  task: WorkTask | OperationalTask;
  startY: number;
  endY: number;
};

export type IntersectionResult = {
  taskAbove: WorkTask | OperationalTask | null;
  taskBelow: WorkTask | OperationalTask | null;
  intersectingTask: WorkTask | OperationalTask | null;
  isCloserToStart: boolean;
  // For UI purposes: which single task should show the blue indicator
  targetTaskForIndicator: WorkTask | OperationalTask | null;
  showIndicatorBefore: boolean;
};

class IntervalTreeNode {
  center: number;
  intervals: TaskInterval[] = [];
  left: IntervalTreeNode | null = null;
  right: IntervalTreeNode | null = null;
  private taskYCoordinates: Map<
    WorkTask | OperationalTask,
    { startY: number; endY: number }
  >;

  constructor(
    intervals: TaskInterval[],
    taskYCoordinates: Map<
      WorkTask | OperationalTask,
      { startY: number; endY: number }
    >,
  ) {
    this.taskYCoordinates = taskYCoordinates;
    if (intervals.length === 0) {
      this.center = 0;
      return;
    }

    // Calculate center as median of interval centers for better balance
    const centers = intervals.map(
      (interval) => (interval.startY + interval.endY) / 2,
    );
    centers.sort((a, b) => a - b);
    this.center = centers[Math.floor(centers.length / 2)];

    // Partition intervals
    const leftIntervals: TaskInterval[] = [];
    const rightIntervals: TaskInterval[] = [];
    const centerIntervals: TaskInterval[] = [];

    for (const interval of intervals) {
      if (interval.endY < this.center) {
        leftIntervals.push(interval);
      } else if (interval.startY > this.center) {
        rightIntervals.push(interval);
      } else {
        // This interval crosses the center point
        centerIntervals.push(interval);
      }
    }

    // Store intervals that cross the center, sorted by start time
    this.intervals = centerIntervals.sort((a, b) => a.startY - b.startY);

    // Build subtrees
    if (leftIntervals.length > 0) {
      this.left = new IntervalTreeNode(leftIntervals, taskYCoordinates);
    }
    if (rightIntervals.length > 0) {
      this.right = new IntervalTreeNode(rightIntervals, taskYCoordinates);
    }
  }

  query(y: number, excludeTaskId?: number | null): IntersectionResult {
    let taskAbove: WorkTask | OperationalTask | null = null;
    let taskBelow: WorkTask | OperationalTask | null = null;
    let intersectingTask: WorkTask | OperationalTask | null = null;
    let isCloserToStart = false;
    let targetTaskForIndicator: WorkTask | OperationalTask | null = null;
    let showIndicatorBefore = false;

    // Check intervals that cross the center
    for (const interval of this.intervals) {
      // Skip task if it matches the excluded task ID
      if (
        excludeTaskId !== undefined &&
        excludeTaskId !== null &&
        interval.task.idTask === excludeTaskId
      ) {
        continue;
      }

      // Track closest task above (interval ends before y)
      if (interval.endY < y) {
        if (!taskAbove || interval.endY > this.getTaskEndY(taskAbove)) {
          taskAbove = interval.task;
        }
      }

      // Track closest task below (interval starts after y)
      if (interval.startY > y) {
        if (!taskBelow || interval.startY < this.getTaskStartY(taskBelow)) {
          taskBelow = interval.task;
        }
      }

      // Check if y is within this interval
      if (!taskAbove && taskBelow) {
        isCloserToStart = true;
      } else if (y >= interval.startY && y <= interval.endY) {
        intersectingTask = interval.task;
        // Calculate if closer to start or end
        const distanceToStart = y - interval.startY;
        const distanceToEnd = interval.endY - y;
        isCloserToStart = distanceToStart <= distanceToEnd;
        break; // Take the first intersecting task
      }
    }

    // If we found an intersecting task, we still need to check subtrees for closer above/below tasks

    // Search left subtree
    if (this.left) {
      const leftResult = this.left.query(y, excludeTaskId);

      // If left subtree found an intersecting task and we don't have one, use it
      if (leftResult.intersectingTask && !intersectingTask) {
        intersectingTask = leftResult.intersectingTask;
        isCloserToStart = leftResult.isCloserToStart;
      }

      // Merge above/below results - always take the closest
      if (leftResult.taskAbove) {
        if (
          !taskAbove ||
          this.getTaskEndY(leftResult.taskAbove) > this.getTaskEndY(taskAbove)
        ) {
          taskAbove = leftResult.taskAbove;
        }
      }

      if (leftResult.taskBelow) {
        if (
          !taskBelow ||
          this.getTaskStartY(leftResult.taskBelow) <
            this.getTaskStartY(taskBelow)
        ) {
          taskBelow = leftResult.taskBelow;
        }
      }
    }

    // Search right subtree
    if (this.right) {
      const rightResult = this.right.query(y, excludeTaskId);

      // If right subtree found an intersecting task and we don't have one, use it
      if (rightResult.intersectingTask && !intersectingTask) {
        intersectingTask = rightResult.intersectingTask;
        isCloserToStart = rightResult.isCloserToStart;
      }

      // Merge above/below results - always take the closest
      if (rightResult.taskAbove) {
        if (
          !taskAbove ||
          this.getTaskEndY(rightResult.taskAbove) > this.getTaskEndY(taskAbove)
        ) {
          taskAbove = rightResult.taskAbove;
        }
      }

      if (rightResult.taskBelow) {
        if (
          !taskBelow ||
          this.getTaskStartY(rightResult.taskBelow) <
            this.getTaskStartY(taskBelow)
        ) {
          taskBelow = rightResult.taskBelow;
        }
      }
    }

    // Calculate target task for UI indicator based on proximity
    if (intersectingTask && !taskAbove && isCloserToStart) {
      targetTaskForIndicator = intersectingTask;
      showIndicatorBefore = isCloserToStart;
    } else if (intersectingTask && !isCloserToStart) {
      // If intersecting with a task, that task gets the indicator
      targetTaskForIndicator = intersectingTask;
      showIndicatorBefore = isCloserToStart;
    } else if (intersectingTask && isCloserToStart) {
      targetTaskForIndicator = taskAbove;
      showIndicatorBefore = !isCloserToStart;
    } else if (!intersectingTask && taskAbove && taskBelow) {
      /* Since task is always placed as early as possible, 
      the indicator line should always shoul under taskAbove */
      targetTaskForIndicator = taskAbove;
      showIndicatorBefore = false;
    } else if (taskAbove) {
      // Only taskAbove exists - show indicator after it
      targetTaskForIndicator = taskAbove;
      showIndicatorBefore = false;
    } else if (taskBelow) {
      // Only taskBelow exists - show indicator before it
      targetTaskForIndicator = taskBelow;
      showIndicatorBefore = true;
    } else {
    }

    return {
      taskAbove,
      taskBelow,
      intersectingTask,
      isCloserToStart,
      targetTaskForIndicator,
      showIndicatorBefore,
    };
  }

  private getTaskStartY(task: WorkTask | OperationalTask): number {
    const coords = this.taskYCoordinates.get(task);
    return coords ? coords.startY : 0;
  }

  private getTaskEndY(task: WorkTask | OperationalTask): number {
    const coords = this.taskYCoordinates.get(task);
    return coords ? coords.endY : 0;
  }
}

class IntervalTree {
  private root: IntervalTreeNode | null = null;
  private dateToYCoordinate: (dateStr: string) => number;
  private taskYCoordinates: Map<
    WorkTask | OperationalTask,
    { startY: number; endY: number }
  > = new Map();

  constructor(
    tasks: (WorkTask | OperationalTask)[],
    dateToYCoordinate: (dateStr: string) => number,
  ) {
    this.dateToYCoordinate = dateToYCoordinate;

    if (tasks.length === 0) return;

    // Convert tasks to intervals and store coordinates for later use
    const intervals: TaskInterval[] = tasks.map((task) => {
      const startY = dateToYCoordinate(task.start);
      const endY = dateToYCoordinate(task.end);

      // Store coordinates for later reference
      this.taskYCoordinates.set(task, { startY, endY });

      return {
        task,
        startY,
        endY,
      };
    });

    this.root = new IntervalTreeNode(intervals, this.taskYCoordinates);
  }

  query(y: number, excludeTaskId?: number | null): IntersectionResult {
    if (!this.root) {
      return {
        taskAbove: null,
        taskBelow: null,
        intersectingTask: null,
        isCloserToStart: false,
        targetTaskForIndicator: null,
        showIndicatorBefore: false,
      };
    }

    const result = this.root.query(y, excludeTaskId);
    const allTasks = this.getAllTasks();

    // If we don't have either taskAbove or taskBelow, but we have tasks in this column,
    // find the nearest task regardless of distance to ensure blue indicator appears
    if (!result.taskAbove && !result.taskBelow && !result.intersectingTask) {
      if (allTasks.length > 0) {
        // Find the task with the smallest distance to the query position
        let nearestTask: TaskInterval | null = null;
        let minDistance = Infinity;
        let isAbove = false;

        for (const task of allTasks) {
          // Skip task if it matches the excluded task ID
          if (
            excludeTaskId !== undefined &&
            excludeTaskId !== null &&
            task.task.idTask === excludeTaskId
          ) {
            continue;
          }

          const taskCenter = (task.startY + task.endY) / 2;
          const distance = Math.abs(taskCenter - y);

          if (distance < minDistance) {
            minDistance = distance;
            nearestTask = task;
            isAbove = taskCenter < y;
          }
        }

        if (nearestTask) {
          if (isAbove) {
            return {
              taskAbove: nearestTask.task,
              taskBelow: null,
              intersectingTask: null,
              isCloserToStart: false,
              targetTaskForIndicator: nearestTask.task,
              showIndicatorBefore: false, // Show after the task above
            };
          } else {
            return {
              taskAbove: null,
              taskBelow: nearestTask.task,
              intersectingTask: null,
              isCloserToStart: false,
              targetTaskForIndicator: nearestTask.task,
              showIndicatorBefore: true, // Show before the task below
            };
          }
        }
      }
    }

    return result;
  }

  // Helper method to query by date string
  queryByDate(dateStr: string): IntersectionResult {
    const y = this.dateToYCoordinate(dateStr);
    return this.query(y);
  }

  // Debug method to help troubleshoot
  getAllTasks(): TaskInterval[] {
    const allTasks: TaskInterval[] = [];
    this.taskYCoordinates.forEach((coords, task) => {
      allTasks.push({
        task,
        startY: coords.startY,
        endY: coords.endY,
      });
    });
    return allTasks.sort((a, b) => a.startY - b.startY);
  }
}

class TaskIntersector {
  private employees: Employee[];
  private timelineRange: FullTimelineRange;
  private pixelsPerMinute: number;
  private leftColumnWidth: number;
  private unplannedColumnWidth: number;
  private columnWidth: number;
  private treeMap: Map<number, IntervalTree> = new Map();

  constructor(
    employees: Employee[],
    timelineRange: FullTimelineRange,
    pixelsPerMinute: number,
    leftColumnWidth: number,
    unplannedColumnWidth: number,
    columnWidth: number,
  ) {
    this.employees = employees;
    this.timelineRange = timelineRange;
    this.pixelsPerMinute = pixelsPerMinute;
    this.leftColumnWidth = leftColumnWidth;
    this.unplannedColumnWidth = unplannedColumnWidth;
    this.columnWidth = columnWidth;

    this.constructTreeMap();
  }

  private dateToYCoordinate = (date: string): number => {
    const differenceInMilliseconds =
      new Date(date).getTime() -
      new Date(this.timelineRange.earliestHourDisplayedOnTimeline).getTime();
    const differenceInMinutes = differenceInMilliseconds / 60000;

    return differenceInMinutes * this.pixelsPerMinute;
  };

  private constructTreeMap(): void {
    this.employees.forEach((employee, index) => {
      const tasks = [
        ...employee.tasks.workTasks,
        ...employee.tasks.operationalTasks,
      ];

      this.treeMap.set(index, new IntervalTree(tasks, this.dateToYCoordinate));
    });
  }

  private xCoordinateToColumnIndex = (xPosition: number): number => {
    const index =
      (xPosition - this.unplannedColumnWidth - this.leftColumnWidth) /
      this.columnWidth;
    return Math.floor(Math.max(0, index));
  };

  /**
   * Find the closest tasks above and below the given position
   * @param x - X coordinate (determines employee/column)
   * @param y - Y coordinate (determines time position)
   * @param excludeTaskId - Optional task ID to exclude from intersection results (useful when dragging a task)
   * @returns Object containing taskAbove, taskBelow, and intersectingTask
   */
  intersect(
    x: number,
    y: number,
    excludeTaskId?: number | null,
  ): IntersectionResult | null {
    const columnIndex = this.xCoordinateToColumnIndex(x);
    const tree = this.treeMap.get(columnIndex);

    if (!tree) {
      return null;
    }

    const result = tree.query(y, excludeTaskId);
    return result;
  }

  /**
   * Get all tasks for a specific employee/column
   * @param columnIndex - The employee index
   * @returns Array of all tasks for that employee
   */
  getTasksForColumn(columnIndex: number): (WorkTask | OperationalTask)[] {
    if (columnIndex < 0 || columnIndex >= this.employees.length) {
      return [];
    }

    const employee = this.employees[columnIndex];
    return [...employee.tasks.workTasks, ...employee.tasks.operationalTasks];
  }

  /**
   * Find the optimal insertion point for a new task
   * @param x - X coordinate
   * @param y - Y coordinate
   * @returns Information about where to insert the task
   */
  findInsertionPoint(
    x: number,
    y: number,
  ): {
    columnIndex: number;
    insertAfter: WorkTask | OperationalTask | null;
    insertBefore: WorkTask | OperationalTask | null;
  } {
    const columnIndex = this.xCoordinateToColumnIndex(x);
    const result = this.intersect(x, y);

    return {
      columnIndex,
      insertAfter: result?.taskAbove || null,
      insertBefore: result?.taskBelow || null,
    };
  }
}

export default TaskIntersector;
